import { Route } from 'vue-router';
import Vue from 'vue';
import { Guard } from '../../../guards/guardWrapper';
import ServiceResponse from '../../../servicelayer/responseTypes/serviceResponse';
import { ApiMethod } from '../../../servicelayer/apiMethod';
import { defaultRedirectParamName } from '../../../router/routerUtils';

async function checkNeedsAuth(route: any) {
  const needsAuth = await Vue.$routerUtils.getRouteOption<boolean>(route, 'auth');
  const needsRole = await Vue.$routerUtils.getRouteOption<string>(route, 'role');
  return needsAuth === true || (needsRole !== undefined && needsRole !== null);
}

export default {
  name: 'BearerTokenGuard',
  registerInterceptor() {
    return {
      name: 'Auth guard, check if redirect to login needed',
      async request(requestType: ApiMethod, apiEndpoint: string) {
        if (!Vue.$pwa.isOnline) {
          // If the app is offline and the request interceptor is called it won't succeed anyway
          // This way offline apps will still work because they get the data from cache
          return undefined;
        }
        try {
          const needsAuth = await checkNeedsAuth(Vue.$router.currentRoute);
          if (needsAuth) {
            if (Vue.$auth.isLoggedIn && Vue.$auth.needsRefresh) {
              const needsLogin = await Vue.$auth.refresh();
              if (needsLogin) {
                return await Vue.$auth.redirectToLogin();
              }
            }
          }
        } catch (e) { Vue.$log.error(e); }
        return undefined;
      },
      async response(response: ServiceResponse) {
        if (!Vue.$pwa.isOnline) {
          // If the app is offline and the response interceptor is called it is very likely cached
          // Don't try and refresh
          return undefined;
        }
        try {
          const needsAuth = await checkNeedsAuth(Vue.$router.currentRoute);
          if (needsAuth) {
            if (response.status === 401) {
              const needsLogin = await Vue.$auth.refresh();
              if (needsLogin) {
                return await Vue.$auth.redirectToLogin();
              }
            }
          }
        } catch (e) { Vue.$log.error(e); }
        return undefined;
      },
    };
  },
  async routeBeforeEnter(to: Route, from: Route) {
    await Vue.$config.promise;
    if (!Vue.$pwa.isOnline) {
      // No need to refresh, since we are offline
      // This way offline apps will still work
      return undefined;
    }
    const needsAuth = await checkNeedsAuth(to);
    if (!needsAuth) {
      return undefined;
    }
    let needsLogin = false;
    if (Vue.$auth.needsRefresh) {
      needsLogin = await Vue.$auth.refresh();
      if (needsLogin) {
        return Vue.$routerUtils.addQueryToUrl(Vue.$config.values['auth-redirects-login'], { [defaultRedirectParamName]: to.fullPath });
      }
    }
    // The user info is not saved in storage, only in memory, so fetch new instance if not set
    if (!Vue.$auth.user) {
      await Vue.$auth.getUser();
    }
    return undefined;
  },
} as Guard;
